import Character from 'assets/images/characters/CarolinaMariadeJesus/default-happily.webp';
import {useEffect, useMemo, useState} from 'react';
import {useMobile} from 'utils/hooks';
import StoreRatingModal from 'containers/SatisfactionRating/StoreRatingModal';
import DesktopStoreRatingModal from 'containers/SatisfactionRating/DesktopStoreRatingModal';
import SidebarLayout from 'layouts/SidebarLayout';
import getFeatureFlag from 'utils/functions/getFeatureFlags';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {useModals} from 'letrus-ui';
import {updateProfileService} from 'store/services/profileServices';
import {fetchActivitiesRequest} from 'APIs/letrusAPI/activities';
import {Activity} from 'APIs/letrusAPI/activities/types';
import {fetchNotificationRequest} from 'APIs/letrusAPI/notification';
import TourProvider from 'features/TourProvider/TourProvider';
import {useTour} from 'utils/hooks/useTour';
import useShouldShowContestResult from 'utils/hooks/useShouldShowContestResult';
import {getOnboardingTourSteps} from 'components/PageWrapper/OnboardingTourSteps';
import {useCloseTransferNotice} from 'features/useCloseTransferNotice';
import {useShowTransferNotice} from 'features/useShowTransferNotice';
import {fetchUserProfileRequest} from 'APIs/letrusAPI/user';
import DashboardCard from './components/DashboardCard';
import ActivityTagList from './components/ActivityTagList';
import styles from './Dashboard.module.scss';
import OCRTourModal from './components/OCRTourModal';
import TransferNoticeModal from './components/TransferNoticeModal';
import ReviewedReminder from './components/ReviewedReminder';
import EvolutionRedirect from './components/EvolutionRedirect';
import ResetPasswordModal from './modals/ResetPasswordModal';
import UserInfo from './components/UserInfo';
import CTABannersCarousel from './CTABanners/CTABannersCarousel';
import FlagrAlert from './components/FlagrAlert';
import B2CBanner from './components/B2CBanner';

function Dashboard(): JSX.Element {
  const {isMobile} = useMobile();
  const {userRole, user, userProfile} = getUserData();
  const {shouldShowContestResult} = useShouldShowContestResult();

  const {data: activities} = fetchActivitiesRequest();
  const {data: notificationData} = fetchNotificationRequest();
  // recent_transfer_notice (BO-2277) isn't on the cached session (getUserData
  // above) — it's deliberately excluded from the login response to avoid an
  // extra query for every user, so it's fetched here instead, only for the
  // student's own Dashboard load.
  const {data: userProfileData} = fetchUserProfileRequest();
  const transferNoticeProfile = userProfileData?.results[0];

  const modals = useModals([
    'ocrTourModal',
    'storeRatingModal',
    'desktopStoreRatingModal',
    'transferNoticeModal',
  ]);

  // Track if password reset modal is open
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  // Initialize tour state and persistence
  const tour = useTour({storageKey: 'hasClosedDashboardTour'});

  const isEmptyScheduleActivities = activities?.scheduled?.length === 0;
  const isEmptyFinishedActivities =
    activities?.finished?.length === 0 &&
    activities?.reviewed?.length === 0 &&
    activities?.expired?.length === 0;

  const isAppReviewPopupOn = getFeatureFlag('app_review_popup').isOn;
  const isOCRTourExpirimentOn = getFeatureFlag('ocr_tour_modal').isOn;
  const isEvolutionOn = getFeatureFlag('evolution_portal').isOn;

  const shouldShowSidebar = getFeatureFlag(
    'student_recommendations_and_alerts',
  ).isOn;

  const openActivitiesGroups = useMemo(() => {
    const allOpenActivities = [
      ...(activities?.in_progress || []),
      ...(activities?.opened || []),
    ].filter((activity) => activity.learning_path_type !== 'special');

    const required = allOpenActivities.filter(
      (activity) => activity.is_required === true,
    );
    const optional = allOpenActivities.filter(
      (activity) => activity.is_required !== true,
    );

    return {required, optional};
  }, [activities?.in_progress, activities?.opened]);

  const isEmptyOpenActivities =
    openActivitiesGroups.required.length === 0 &&
    openActivitiesGroups.optional.length === 0;

  const areAllActivitiesEmpty =
    isEmptyOpenActivities &&
    isEmptyFinishedActivities &&
    isEmptyScheduleActivities;

  function renderActivityCard(activity: Activity, showStartHere = false) {
    return (
      <li key={activity.id}>
        <DashboardCard
          activity={activity}
          closeTour={tour.closeTour}
          tags={
            <ActivityTagList
              activity={activity}
              showStartHere={showStartHere}
            />
          }
        >
          {activity?.last_review_goal && (
            <ReviewedReminder
              data-id="reminder"
              icon="bullseye-arrow"
              text={`Meta: evoluir ${
                activity?.last_review_goal?.competence_name.startsWith(
                  'Competência',
                )
                  ? 'na'
                  : 'no'
              } ${activity?.last_review_goal?.competence_name}`}
            />
          )}
        </DashboardCard>
      </li>
    );
  }

  useEffect(() => {
    if (isOCRTourExpirimentOn && modals.ocrTourModal) {
      if (sessionStorage.getItem('hasShownOCRTourModal') === null) {
        modals.ocrTourModal?.showModal();
        sessionStorage.setItem('hasShownOCRTourModal', 'true');
      }
    }
  }, [isOCRTourExpirimentOn, modals.ocrTourModal]);

  // Shows the class-transfer notice on first Dashboard load after the
  // student is transferred, until they acknowledge it (BO-2277). The
  // backend already excludes transfers already flagged as seen in
  // tour_flags, so any non-null value here means "show it". Dialog control
  // stays with the parent (this component) via useModals, per this
  // project's modal convention — TransferNoticeModal itself has no internal
  // ref.
  useShowTransferNotice(transferNoticeProfile, modals.transferNoticeModal);

  // Persists the "seen" flag by listening to the dialog's native `close`
  // event (registered internally by the hook on modals.transferNoticeModal)
  // — catches every way the modal can close (the "Entendi" button, the
  // built-in close button, ESC), not just an explicit button click.
  useCloseTransferNotice(transferNoticeProfile, modals.transferNoticeModal);

  useEffect(() => {
    if (
      isAppReviewPopupOn &&
      (modals.storeRatingModal || modals.desktopStoreRatingModal)
    ) {
      if (sessionStorage.getItem('shouldOpenStoreRatingModal') === null) {
        if (isMobile) {
          modals.storeRatingModal?.showModal();
        } else {
          modals.desktopStoreRatingModal?.showModal();
        }
        sessionStorage.setItem('shouldOpenStoreRatingModal', 'true');
      }
    }
  }, [
    isAppReviewPopupOn,
    isMobile,
    modals.storeRatingModal,
    modals.desktopStoreRatingModal,
  ]);

  useEffect(() => {
    if (notificationData?.not_seen_by_teacher) {
      const unseenNotificationText = `(${
        notificationData?.not_seen_by_teacher > 99
          ? '99+'
          : notificationData?.not_seen_by_teacher
      })`;

      document.title = `${
        notificationData?.not_seen_by_teacher ? unseenNotificationText : ''
      } Letrus`;
    }
  }, [notificationData]);

  useEffect(() => {
    const isPre = userRole?.school_group_is_pre;

    (async () => {
      if (!userProfile?.flag_first_access) {
        try {
          await updateProfileService({
            id: userProfile?.id,
            flag_first_access: !isPre,
            flag_pre_access: userProfile?.flag_pre_access || isPre,
          });
        } catch (error) {
          console.error(error);
        }
      }
    })();

    // Open tour if it's first access, hasn't been closed, and password modal is not open
    if (
      !userProfile?.flag_first_access &&
      tour.isOpen === false &&
      !isPasswordModalOpen
    ) {
      tour.openTour();
    } else {
      tour.closeTour();
    }
  }, [userProfile?.flag_first_access, isPasswordModalOpen]);

  return (
    <>
      <SidebarLayout>
        <main className={styles.wrapper}>
          <B2CBanner />
          <FlagrAlert />
          <CTABannersCarousel
            shouldShowContestResult={shouldShowContestResult}
          />
          <UserInfo userRole={userRole} user={user} />

          {isEvolutionOn &&
            !shouldShowSidebar &&
            !getFeatureFlag('c5_banner').isOn && <EvolutionRedirect />}

          <section aria-labelledby="activities-title">
            <h1 id="activities-title" className="visually-hidden">
              Suas Atividades
            </h1>
            {areAllActivitiesEmpty ? (
              <div className={styles.allEmptyActivities}>
                <figure aria-hidden="true">
                  <img src={Character} alt="" />
                </figure>
                <strong>
                  Você não possui nenhuma atividade no momento. Volte em breve!
                </strong>
              </div>
            ) : (
              <ul className={styles.activityList}>
                <li data-focusid="openActivities">
                  <h2>Atividades abertas</h2>
                  {isEmptyOpenActivities ? (
                    <div className={styles.emptyActivities}>
                      <strong>
                        Você não possui nenhuma atividade aberta no momento
                      </strong>
                    </div>
                  ) : (
                    <>
                      {openActivitiesGroups?.required?.length > 0 && (
                        <section>
                          <h3 className={styles.subsectionTitle}>
                            Obrigatórias (
                            {openActivitiesGroups?.required?.length})
                          </h3>
                          <ul>
                            {openActivitiesGroups?.required?.map(
                              (activity, index) =>
                                renderActivityCard(activity, index === 0),
                            )}
                          </ul>
                        </section>
                      )}

                      {openActivitiesGroups?.optional?.length > 0 && (
                        <section>
                          <h3 className={styles.subsectionTitle}>
                            Opcionais ({openActivitiesGroups?.optional?.length})
                          </h3>
                          <ul>
                            {openActivitiesGroups?.optional?.map((activity) =>
                              renderActivityCard(activity, false),
                            )}
                          </ul>
                        </section>
                      )}
                    </>
                  )}
                </li>

                <li data-focusid="scheduledOrPastActivities">
                  <h2>Atividades agendadas</h2>
                  {isEmptyScheduleActivities ? (
                    <div className={styles.emptyActivities}>
                      <strong>
                        Você não possui nenhuma atividade agendada no momento
                      </strong>
                    </div>
                  ) : (
                    <ul>
                      {activities?.scheduled
                        ?.filter(
                          (activity) =>
                            activity.learning_path_type !== 'special',
                        )
                        ?.map((scheduledActivity) => (
                          <li key={scheduledActivity.id}>
                            <DashboardCard
                              activity={scheduledActivity}
                              tags={
                                <ActivityTagList activity={scheduledActivity} />
                              }
                            />
                          </li>
                        ))}
                    </ul>
                  )}
                </li>

                <li data-focusid="scheduledOrPastActivities">
                  <h2>Atividades passadas</h2>
                  {isEmptyFinishedActivities ? (
                    <div className={styles.emptyActivities}>
                      <strong className={styles.lastEmptyActivities}>
                        Você não possui nenhuma atividade passada no momento
                      </strong>
                    </div>
                  ) : (
                    <ul>
                      {activities?.finished
                        ?.filter(
                          (activity) =>
                            activity.learning_path_type !== 'special',
                        )
                        ?.map((finishedActivity) => (
                          <li key={finishedActivity.id}>
                            <DashboardCard
                              activity={finishedActivity}
                              tags={
                                <ActivityTagList activity={finishedActivity} />
                              }
                            />
                          </li>
                        ))}

                      {activities?.reviewed
                        ?.filter(
                          (activity) =>
                            activity.learning_path_type !== 'special',
                        )
                        ?.map((reviewedActivity) => (
                          <li key={reviewedActivity.id}>
                            <DashboardCard
                              activity={reviewedActivity}
                              tags={
                                <ActivityTagList activity={reviewedActivity} />
                              }
                            />
                          </li>
                        ))}

                      {activities?.expired
                        ?.filter(
                          (activity) =>
                            activity.learning_path_type !== 'special',
                        )
                        ?.map((expiredActivity) => (
                          <li key={expiredActivity.id}>
                            <DashboardCard
                              activity={expiredActivity}
                              tags={
                                <ActivityTagList activity={expiredActivity} />
                              }
                            />
                          </li>
                        ))}
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </section>
        </main>

        <OCRTourModal onClickButton={() => modals.ocrTourModal?.close()} />

        {transferNoticeProfile?.recent_transfer_notice && (
          <TransferNoticeModal
            previousSchoolGroup={
              transferNoticeProfile.recent_transfer_notice.previous_school_group
            }
            onClose={() => modals.transferNoticeModal?.close()}
          />
        )}

        <StoreRatingModal
          showBothStoresButton
          onClose={() => {
            modals.storeRatingModal?.close();
            sessionStorage.setItem('shouldOpenStoreRatingModal', 'false');
          }}
        />

        <DesktopStoreRatingModal />

        <ResetPasswordModal
          onModalOpen={() => setIsPasswordModalOpen(true)}
          onModalClose={() => {
            setIsPasswordModalOpen(false);
            // Open tour after password modal closes if it's first access
            if (!userProfile?.flag_first_access && !tour.isOpen) {
              tour.openTour();
            }
          }}
        />
      </SidebarLayout>

      <TourProvider
        steps={getOnboardingTourSteps(
          !!activities?.in_progress?.filter(
            (activity) => activity?.learning_path_type !== 'special',
          ).length,
          !!activities?.opened?.filter(
            (activity) => activity?.learning_path_type !== 'special',
          ).length,
        )}
        isOpen={tour.isOpen}
        onRequestClose={tour.onRequestClose}
        accentColor="#ffbf00"
        showNumber
        showNavigation
        showNavigationNumber
        disableInteraction={false}
      />
    </>
  );
}

export default Dashboard;
